import React, { PureComponent } from 'react'
import {
  Modal,
  Descriptions,
  Tag,
  Button,
  Spin,
  notification,
  Select,
  InputNumber,
  Form,
} from 'antd'
import moment from 'moment'
import Request from '../../../request'
import MediaViewer from '../../../components/MediaViewer'
import styles from './styles.less'

const { Option } = Select

const parseMedia = (data) => {
  if (!data) return { photos: [], videos: [] }
  if (Array.isArray(data)) {
    const photos = []
    const videos = []
    data.forEach((m) => {
      const type = (m && m.type) || ''
      if (type === 'video') videos.push(m)
      else if (type === 'image' || type === 'photo') photos.push(m)
      else photos.push(m)
    })
    return { photos, videos }
  }
  return {
    photos: data.photos || data.images || [],
    videos: data.videos || [],
  }
}

const emptyStockModal = {
  visible: false,
  mediaId: null,
  productId: undefined,
  productName: '',
  quantity: undefined,
  unit: '',
  saving: false,
  itemOrderId: '',
  itemUser: '',
  itemSubCategory: '',
  itemType: '',
}

// Outward review is driven by Capture Date — a click on a date group shows all
// outward media captured that day, regardless of Order ID.
class OutwardReviewModal extends PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      mediaLoading: false,
      photos: [],
      videos: [],
      products: [],
      stock: {}, // mediaId -> { saved, productName, quantity, unit }
      stockModal: { ...emptyStockModal },
    }
    this.dirty = false
  }

  componentDidMount() {
    this.fetchProducts()
  }

  componentDidUpdate(prevProps) {
    if (this.props.visible && !prevProps.visible && this.props.group) {
      this.dirty = false
      this.setState({ stock: {}, stockModal: { ...emptyStockModal } })
      this.fetchMedia()
    }
  }

  // Products come from the stock-product master:
  // GET https://sales-r.ht-india.com/stockProduct?results=2000
  fetchProducts = () => {
    const pickList = (res) =>
      !res ? [] : Array.isArray(res) ? res : res.data || res.products || []
    Request.getStockProductMaster({ results: 2000 }).then((res) => {
      this.setState({ products: pickList(res) })
    })
  }

  fetchMedia = () => {
    const { group } = this.props
    if (!group || !group.key) return
    const startDate = moment(group.key, 'YYYY-MM-DD').startOf('day').toISOString()
    const endDate = moment(group.key, 'YYYY-MM-DD').endOf('day').toISOString()
    this.setState({ mediaLoading: true, photos: [], videos: [] })
    Request.outwardMediaByDate({
      captureDate: group.key,
      startDate,
      endDate,
      orderIds: (group.orderIds || []).join(','),
      _t: Date.now(),
    }).then(({ error, data }) => {
      if (!error) {
        const { photos, videos } = parseMedia(data)
        const stock = {}
        ;[...photos, ...videos].forEach((m) => {
          if (m && m._id && m.movedToStock) {
            stock[m._id] = {
              saved: true,
              productName: m.stockProductName,
              quantity: m.stockQuantity,
              unit: m.stockUnit,
            }
          }
        })
        this.setState({ mediaLoading: false, photos, videos, stock })
      } else {
        this.setState({ mediaLoading: false })
        notification.error({ message: 'Failed to load media' })
      }
    })
  }

  handleItemReview = async (item, status, remarks) => {
    if (!item || !item._id) {
      notification.error({ message: 'Missing media id' })
      throw new Error('missing media id')
    }
    let res
    if (status === 'Rejected') {
      res = await Request.rejectOutwardMedia({ mediaId: item._id, remarks: remarks || '' })
    } else {
      res = await Request.completeOutwardMedia({ mediaId: item._id })
    }
    if (res.error) {
      notification.error({ message: res.message || 'Failed to update media' })
      throw new Error('review failed')
    }
    notification.success({ message: res.message || `Media marked as ${status}` })
    this.dirty = true
    return status
  }

  handleClose = () => {
    const { onUpdated, onClose } = this.props
    if (this.dirty && onUpdated) onUpdated()
    this.dirty = false
    if (onClose) onClose()
  }

  // ---- Move To Stock (modal) ----
  openStock = (item) => {
    const m = (item && item.raw) || {}
    if (!this.state.products.length) this.fetchProducts()
    this.setState({
      stockModal: {
        ...emptyStockModal,
        visible: true,
        mediaId: item._id,
        itemOrderId: m.orderId || '',
        itemUser: m.userName || '',
        itemSubCategory: m.subCategory || '',
        itemType: item.type || '',
      },
    })
  }

  closeStock = () => {
    this.setState({ stockModal: { ...emptyStockModal } })
  }

  setStockField = (patch) => {
    this.setState((s) => ({ stockModal: { ...s.stockModal, ...patch } }))
  }

  selectStockProduct = (productId) => {
    const product = this.state.products.find((p) => p._id === productId)
    this.setStockField({
      productId,
      productName: product ? product.name : '',
      unit: product ? product.unit || '' : '',
    })
  }

  saveStock = async () => {
    const { mediaId, productId, productName, quantity, unit } = this.state.stockModal
    if (!productId) {
      notification.error({ message: 'Please select a product' })
      return
    }
    if (!quantity || Number(quantity) <= 0) {
      notification.error({ message: 'Please enter a valid quantity' })
      return
    }
    this.setStockField({ saving: true })
    // 1. Existing Stock Outward API — creates the real stock-movement entry
    const res = await Request.addInward({
      recordDate: moment().toISOString(),
      type: 'Outward',
      totalData: [
        { productId, product: productName, quantity, description: '' },
      ],
    })
    if (!res.error) {
      // 2. Audit on the photo doc so the "In stock" tag persists across reloads
      await Request.moveOutwardMediaToStock({ mediaId, productId, productName, quantity, unit })

      notification.success({ message: res.message || 'Moved to stock' })
      this.setState((s) => ({
        stock: {
          ...s.stock,
          [mediaId]: { saved: true, productName, quantity, unit },
        },
        stockModal: { ...emptyStockModal },
      }))
      this.dirty = true
      // refresh this screen's data (modal media + the list behind it)
      this.fetchMedia()
      if (this.props.onUpdated) this.props.onUpdated()
    } else {
      notification.error({ message: res.message || 'Failed to move to stock' })
      this.setStockField({ saving: false })
    }
  }

  renderItemExtra = (item) => {
    const m = (item && item.raw) || {}
    const saved = this.state.stock[item._id]
    return (
      <div>
        {(m.orderId || m.userName || m.subCategory) && (
          <div className={styles.itemMeta}>
            {m.orderId ? <span className={styles.itemMetaPart}>Order #{m.orderId}</span> : null}
            {m.userName ? <span className={styles.itemMetaPart}>{m.userName}</span> : null}
            {m.subCategory ? <span className={styles.itemMetaPart}>{m.subCategory}</span> : null}
          </div>
        )}

        {item.status === 'Completed' && (
          <div className={styles.moveToStock}>
            {saved && saved.saved ? (
              <Tag color="green" className={styles.stockSavedTag}>
                In stock: {saved.productName}
                {saved.quantity ? ` × ${saved.quantity}` : ''}
              </Tag>
            ) : (
              <Button
                size="small"
                icon="plus"
                type="primary"
                ghost
                onClick={() => this.openStock(item)}
              >
                Move To Stock
              </Button>
            )}
          </div>
        )}
      </div>
    )
  }

  renderStockModal = () => {
    const { products, stockModal } = this.state
    const {
      visible,
      productId,
      quantity,
      saving,
      itemOrderId,
      itemUser,
      itemSubCategory,
      itemType,
    } = stockModal
    return (
      <Modal
        title="Move To Stock"
        visible={visible}
        onCancel={this.closeStock}
        onOk={this.saveStock}
        confirmLoading={saving}
        okText="Save to Stock"
        destroyOnClose
        zIndex={1200}
      >
        <p style={{ color: '#888', marginBottom: 12 }}>
          Add this approved {itemType || 'item'} into stock inventory. Select the
          matching product and enter the quantity.
        </p>

        <Descriptions size="small" column={1} bordered className={styles.stockDetails}>
          {itemOrderId ? (
            <Descriptions.Item label="Order ID">#{itemOrderId}</Descriptions.Item>
          ) : null}
          {itemUser ? (
            <Descriptions.Item label="Uploaded By">{itemUser}</Descriptions.Item>
          ) : null}
          {itemSubCategory ? (
            <Descriptions.Item label="Subcategory">{itemSubCategory}</Descriptions.Item>
          ) : null}
          <Descriptions.Item label="Media Type">{itemType || '-'}</Descriptions.Item>
        </Descriptions>

        <Form layout="vertical" style={{ marginTop: 14 }}>
          <Form.Item label="Product" required>
            <Select
              showSearch
              allowClear
              placeholder="Select product"
              value={productId}
              optionFilterProp="children"
              style={{ width: '100%' }}
              dropdownStyle={{ zIndex: 1300 }}
              getPopupContainer={(trigger) => trigger.parentNode}
              onChange={this.selectStockProduct}
            >
              {products.map((p) => (
                <Option key={p._id} value={p._id}>
                  {p.name}
                  {p.productCode ? ` (${p.productCode})` : ''}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Quantity" required>
            <InputNumber
              min={1}
              placeholder="Quantity"
              value={quantity}
              style={{ width: '100%' }}
              onChange={(v) => this.setStockField({ quantity: v })}
            />
          </Form.Item>
        </Form>
      </Modal>
    )
  }

  render() {
    const { visible, group } = this.props
    const { mediaLoading, photos, videos } = this.state

    if (!group) return null

    const photosCount = photos.length
    const videosCount = videos.length

    return (
      <Modal
        visible={visible}
        onCancel={this.handleClose}
        footer={null}
        width={'100%'}
        style={{ top: 0, maxWidth: '100%', margin: 0, paddingBottom: 0 }}
        bodyStyle={{ height: 'calc(100vh - 55px)', overflowY: 'auto', padding: '16px 24px' }}
        wrapClassName={styles.fullModalWrap}
        title={
          <span>
            Outward Media — {group.label}{' '}
            <Tag color="blue" style={{ marginLeft: 8 }}>
              {group.mediaCount || photosCount + videosCount} Media
            </Tag>
          </span>
        }
        destroyOnClose
      >
        <Descriptions
          size={'small'}
          column={{ xxl: 4, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }}
          bordered
        >
          <Descriptions.Item label={<b>Capture Date</b>}>{group.label}</Descriptions.Item>
          <Descriptions.Item label={<b>Orders</b>}>{group.orderCount || '-'}</Descriptions.Item>
          <Descriptions.Item label={<b>Photos</b>}>{photosCount}</Descriptions.Item>
          <Descriptions.Item label={<b>Videos</b>}>{videosCount}</Descriptions.Item>
        </Descriptions>

        <div style={{ marginTop: 12, color: '#888' }}>
          Showing all outward photos &amp; videos captured on <b>{group.label}</b>.
          Mark each item <b>Complete</b> or <b>Reject</b> (with a reason).
          Approved items can be moved into stock inventory.
        </div>

        <div style={{ marginTop: 12 }}>
          {mediaLoading ? (
            <div className={styles.mediaLoading}>
              <Spin tip="Loading media..." />
            </div>
          ) : (
            <MediaViewer
              reviewable
              rejectWithReason
              photos={photos}
              videos={videos}
              onReview={this.handleItemReview}
              renderItemExtra={this.renderItemExtra}
              isLocked={(item) =>
                !!(
                  (this.state.stock[item._id] && this.state.stock[item._id].saved) ||
                  (item.raw && item.raw.movedToStock)
                )
              }
            />
          )}
        </div>

        <div className={styles.modalFooter} style={{ textAlign: 'center' }}>
          <Button onClick={this.handleClose}>Close</Button>
        </div>

        {this.renderStockModal()}
      </Modal>
    )
  }
}

export default OutwardReviewModal
